/**
 * GlobalE API Service
 * Handles communication with GlobalE API for free shipping banner data
 */

class GlobalEApiService {
  constructor() {
    // Use production URL by default, can be overridden via window.globalEConfig
    this.baseUrl = window.globalEConfig?.apiUrl
    this.cache = new Map()
    this.cacheExpiry = 5 * 60 * 1000
  }

  /**
   * Check if GlobalE integration is enabled
   * @returns {boolean} True if GlobalE is enabled
   */
  isEnabled() {
    return window.globalEConfig?.enabled === true
  }

  /**
   * Get threshold data from session storage
   * @param {string} currencyCode - Currency code
   * @returns {Object|null} Cached threshold data or null
   */
  getFromSessionStorage(currencyCode) {
    try {
      const key = `${currencyCode}_THRESHOLD_DATA`
      const cached = sessionStorage.getItem(key)
      if (cached) {
        const data = JSON.parse(cached)
        if (data && data.timestamp && Date.now() - data.timestamp < this.cacheExpiry) {
          return data.value
        }
        sessionStorage.removeItem(key)
      }
    } catch (error) {
      console.warn('Failed to read from session storage:', error)
    }
    return null
  }

  /**
   * Save threshold data to session storage
   * @param {string} currencyCode - Currency code
   * @param {Object} data - Threshold data to cache
   */
  saveToSessionStorage(currencyCode, data) {
    try {
      const key = `${currencyCode}_THRESHOLD_DATA`
      const cacheData = {
        value: data,
        timestamp: Date.now()
      }
      sessionStorage.setItem(key, JSON.stringify(cacheData))
    } catch (error) {
      console.warn('Failed to save to session storage:', error)
    }
  }

  /**
   * Get free shipping banner data from GlobalE API
   * @param {string} countryCode - ISO 3166-1 alpha-2 country code (e.g., 'GB')
   * @param {string} currencyCode - ISO 4217 currency code (e.g., 'GBP')
   * @param {string} cultureCode - Culture code (e.g., 'en-GB')
   * @returns {Promise<Object>} Free shipping banner data
   */
  async getFreeShippingBanner(countryCode, currencyCode, cultureCode) {
    if (!this.isEnabled()) {
      return {
        IsEnabled: false,
        CurrencyCode: currencyCode,
        CultureCode: cultureCode,
        CountryCode: countryCode,
        Text: '',
        FreeShippingThreshold: -1
      }
    }

    const sessionData = this.getFromSessionStorage(currencyCode)
    if (sessionData) {
      return sessionData
    }

    const cacheKey = `${countryCode}-${currencyCode}-${cultureCode}`

    const cached = this.cache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data
    }

    try {
      const url = new URL('/api/free-shipping', this.baseUrl)
      url.searchParams.set('countryCode', countryCode)
      url.searchParams.set('currencyCode', currencyCode)
      url.searchParams.set('cultureCode', "")
      url.searchParams.set('client', "12T")

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`GlobalE API Error: ${response.status} - ${errorData.Error || 'Unknown error'}`)
      }

      const data = await response.json()

      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      })

      this.saveToSessionStorage(currencyCode, data)

      return data
    } catch (error) {
      console.error('Failed to fetch GlobalE free shipping data:', error)

      return {
        IsEnabled: false,
        CurrencyCode: currencyCode,
        CultureCode: cultureCode,
        CountryCode: countryCode,
        Text: '',
        FreeShippingThreshold: -1
      }
    }
  }

  /**
   * Get current localization data from Shopify
   * @returns {Object} Current country, currency, and culture codes
   */
  getCurrentLocalization() {
    const country = window.Shopify?.country || 'US'
    const currency = window.Shopify?.currency?.active || 'USD'

    const language = document.documentElement.lang || 'en'
    const culture = `${language}-${country}`

    return {
      countryCode: country,
      currencyCode: currency,
      cultureCode: culture
    }
  }

  /**
   * Format the free shipping threshold for display
   * @param {number} threshold - The threshold amount
   * @param {string} currencyCode - Currency code for formatting
   * @returns {string} Formatted threshold string
   */
  formatThreshold(threshold, currencyCode) {
    if (threshold <= 0) {
      return ''
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(threshold)
  }

  /**
   * Clear the cache (both in-memory and session storage)
   */
  clearCache() {
    this.cache.clear()
    
    try {
      const keys = Object.keys(sessionStorage)
      keys.forEach(key => {
        if (key.endsWith('_THRESHOLD_DATA')) {
          sessionStorage.removeItem(key)
        }
      })
    } catch (error) {
      console.warn('Failed to clear session storage:', error)
    }
  }
}

const globalEApi = new GlobalEApiService()

window.globalEApi = globalEApi
